import { DeeplyReadonly, Filter, Query } from '@cubejs-client/core';
import { useCubeQuery } from '@cubejs-client/react';
import { ArrowDropDown, DescriptionOutlined } from '@mui/icons-material';
import { Box, Button, Dialog, DialogContent, Link } from '@mui/material';
import {
  formatNumber,
  LinearProgressChart,
  LinearProgressList,
  LinearProgressListItem,
  LinearProgressListSubheader,
  NumberFormatPreset,
  Widget,
  WidgetContent,
  WidgetFooter,
  WidgetHeader,
  WidgetIcon,
  useScrollIntoView,
} from '@procureai/ui-mui';
import { isNil, isEmpty } from 'ramda';
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import { UTILIZATION_LIMIT } from '~/common/constants';
import { BaseDomainType, I18nNamespace, ContractQueryField, ContractVariant } from '~/common/types';
import { ButtonSelect } from '~/components/ButtonSelect/ButtonSelect';
import { CircularProgressCentered } from '~/components/CircularProgressCentered/CircularProgressCentered';
import { EncodedQuery } from '~/components/EncodedQuery/EncodedQuery';
import { useModuleContext } from '~/components/ModuleContextProvider';
import { PlaceholderWidget } from '~/components/PlaceholderWidget/PlaceholderWidget';
import { VirtualList } from '~/components/VirtualList/VirtualList';
import { useOpenClose } from '~/hooks';
import { RouteId } from '~/routes';

const MAX_ITEMS = 4;

const tooltipFormatterValue = formatNumber(NumberFormatPreset.CURRENCY);
const tooltipFormatterQuantity = formatNumber(NumberFormatPreset.PERCENT_DECIMAL);

export const ActiveContractsWidget = () => {
  const { entityId } = useModuleContext();
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'ActiveContractsWidget' });
  const [widgetRef, active] = useScrollIntoView({ hash: 'active-contracts' });

  const [dialogProps, dialogOpen] = useOpenClose();
  const [option, setOption] = useState(0);

  const optionVariant = [ContractVariant.VALUE, ContractVariant.QUANTITY][option];

  const optionItems = [
    {
      label: t('volume'),
      value: 0,
    },
    {
      label: t('quantity'),
      value: 1,
    },
  ];

  const filters: Filter[] = [
    {
      member: 'WidgetContracts.supplierId',
      operator: 'equals',
      values: [entityId],
    },
  ];

  const request: DeeplyReadonly<Query> = {
    dimensions: [
      'WidgetContracts.supplierId',
      'WidgetContracts.contractVariant',
      'WidgetContracts.contractId',
      'WidgetContracts.sourceReference',
      'WidgetContracts.contractValidFrom',
      'WidgetContracts.contractValidTo',
      'WidgetContracts.totalSupplierContractCount',
      'WidgetContracts.totalValueContractCount',
      'WidgetContracts.totalQuantityContractCount',
      'ContractUtilization.utilization',
      'ContractUtilization.total',
      'ContractUtilization.spent',
    ],
    order: {
      'WidgetContracts.total': 'desc',
    },
    filters: [
      ...filters,
      {
        member: 'WidgetContracts.contractVariant',
        operator: 'equals',
        values: [optionVariant],
      },
    ],
    segments: ['WidgetContracts.onlyValidContracts'],
  };

  const query = useCubeQuery({ ...request, limit: MAX_ITEMS });

  const nodes = query.resultSet?.rawData()?.map((segment) => ({
    id: segment['WidgetContracts.contractId'],
    name: segment['WidgetContracts.sourceReference'],
    contractValidFrom: segment['WidgetContracts.contractValidFrom'],
    contractValidTo: segment['WidgetContracts.contractValidTo'],
    contractVariant: segment['WidgetContracts.contractVariant'],
    totalCount: segment['WidgetContracts.totalSupplierContractCount'],
    totalValueContracts: segment['WidgetContracts.totalValueContractCount'],
    totalQuantityContracts: segment['WidgetContracts.totalQuantityContractCount'],
    total: segment['ContractUtilization.total'] ?? 0,
    spent: segment['ContractUtilization.spent'] ?? 0,
    utilization: segment['ContractUtilization.utilization'] ?? 0,
  }));

  const isNodesEmpty = isEmpty(nodes) || isNil(nodes);
  const isLoading = query.isLoading;

  const totalVolumeContracts = parseInt(nodes?.[0]?.totalValueContracts) || 0;
  const totalQuantityContracts = parseInt(nodes?.[0]?.totalQuantityContracts) || 0;
  const contractCount = option === 0 ? totalVolumeContracts : totalQuantityContracts;
  const totalCount = totalVolumeContracts + totalQuantityContracts;
  const hasItems = !!contractCount;
  const hasMore = contractCount > MAX_ITEMS;

  const rules = useMemo(() => {
    return [
      { field: ContractQueryField.supplierId, value: entityId },
      { field: ContractQueryField.contractActive, value: true },
    ];
  }, [entityId]);

  const renderItem = useMemo(
    () =>
      ({ item }: { item: any }) => {
        const { id, name, contractValidFrom, contractValidTo, contractVariant, utilization, total, spent } = item;
        const dateRangeText = t('dateRange', {
          dateFrom: new Date(contractValidFrom),
          dateTo: new Date(contractValidTo),
        });
        const barValue = utilization ?? 0;
        const barMax = 1.0;
        const contractColor = barValue > UTILIZATION_LIMIT * barMax ? 'error' : 'primary';
        return (
          <Link
            key={id}
            component={RouterLink}
            to={RouteId.contractDetail(id)}
            underline="none"
            sx={{
              p: contractVariant === ContractVariant.QUANTITY ? 4 : 0,
            }}
          >
            <Box
              style={{ listStyleType: 'none' }}
              sx={{
                py: 4,
              }}
            >
              <LinearProgressListItem
                key={id}
                component="div"
                primary={name}
                primaryRight={
                  contractVariant === ContractVariant.QUANTITY
                    ? dateRangeText
                    : t(!total ? 'value' : 'valueTotal', {
                        spent: spent,
                        total: total,
                      })
                }
                secondaryRight={contractVariant === ContractVariant.QUANTITY ? null : dateRangeText}
              >
                {barValue !== null && barMax !== null && (
                  <LinearProgressChart
                    value={barValue}
                    max={barMax}
                    tooltipFormatter={
                      contractVariant === ContractVariant.VALUE ? tooltipFormatterValue : tooltipFormatterQuantity
                    }
                    color={contractColor}
                    dotColor={contractColor}
                  />
                )}
              </LinearProgressListItem>
            </Box>
          </Link>
        );
      },
    [t],
  );

  return (
    <>
      <Widget ref={widgetRef} active={active} data-intercom-target="ActiveContractsWidget">
        <WidgetHeader
          title={t('title')}
          icon={<WidgetIcon icon={<DescriptionOutlined fontSize="small" />} />}
          actionsRight={
            <ButtonSelect
              endIcon={<ArrowDropDown fontSize="small" />}
              items={optionItems}
              selected={option}
              onItemClick={setOption}
            />
          }
        />
        <WidgetContent>
          {!isNodesEmpty ? (
            <LinearProgressList
              items={nodes}
              renderItem={renderItem}
              subheader={
                hasItems && option === 0 ? (
                  <LinearProgressListSubheader disableGutters disableSticky>
                    {t(option === 0 ? 'listSubheaderVolume' : 'listSubheaderQuantity')}
                  </LinearProgressListSubheader>
                ) : undefined
              }
            />
          ) : isLoading ? (
            <CircularProgressCentered sx={{ position: 'relative' }} />
          ) : (
            <PlaceholderWidget />
          )}
        </WidgetContent>
        <WidgetFooter>
          <Button variant="text" disabled={!hasMore || isNodesEmpty} onClick={dialogOpen}>
            {t('showAll', { count: contractCount || 0 })}
          </Button>
          <EncodedQuery pathname={RouteId.analyticsBase(BaseDomainType.CONTRACT)} rules={rules}>
            {(linkProps) => (
              <Button variant="text" disabled={!hasItems} {...linkProps}>
                {t('showInBase', { count: totalCount })}
              </Button>
            )}
          </EncodedQuery>
        </WidgetFooter>
      </Widget>
      <Dialog
        {...dialogProps}
        sx={{
          '& .MuiDialog-paper': {
            background: 'transparent',
            boxShadow: 'none',
          },
        }}
      >
        <DialogContent>
          <Widget>
            <WidgetHeader
              title={t('title')}
              icon={<WidgetIcon icon={<DescriptionOutlined fontSize="small" />} />}
              actionsRight={
                <ButtonSelect
                  endIcon={<ArrowDropDown fontSize="small" />}
                  items={optionItems}
                  selected={option}
                  onItemClick={setOption}
                />
              }
            />
            <WidgetContent>
              <Box
                sx={{
                  marginBottom: 16,
                }}
              >
                <VirtualList
                  itemSize={76}
                  query={request}
                  queryOptions={{
                    skip: !dialogProps.open,
                    segmentMapper: (segment) => ({
                      id: segment['WidgetContracts.contractId'],
                      name: segment['WidgetContracts.sourceReference'],
                      contractValidFrom: segment['WidgetContracts.contractValidFrom'],
                      contractValidTo: segment['WidgetContracts.contractValidTo'],
                      contractVariant: segment['WidgetContracts.contractVariant'],
                      total: segment['ContractUtilization.total'] ?? 0,
                      spent: segment['ContractUtilization.spent'] ?? 0,
                      utilization: segment['ContractUtilization.utilization'] ?? 0,
                    }),
                    variables: {
                      id: entityId,
                    },
                  }}
                  renderItem={renderItem}
                />
              </Box>
            </WidgetContent>
          </Widget>
        </DialogContent>
      </Dialog>
    </>
  );
};
